import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { FaImages, FaArrowLeft, FaTimes, FaExpand } from "react-icons/fa";
import "./Gallery.css";

import gallery from "../../assets/breadcrumb/Gallery.jpg"; // Your breadcrumb image

// Your backend's URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function Gallery() {
  const [albums, setAlbums] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAndGroupGalleryData = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/gallery/public`);
        const data = await response.json();

        const groupedByFolder = data.reduce((acc, item) => {
          const { folderName } = item;
          if (!acc[folderName]) {
            acc[folderName] = [];
          }
          acc[folderName].push({
            url: `${API_BASE_URL}${item.image}`,
            desc: item.description,
          });
          return acc;
        }, {});

        const albumArray = Object.keys(groupedByFolder).map((title, index) => ({
          id: index + 1,
          title: title,
          images: groupedByFolder[title],
        }));

        setAlbums(albumArray);
      } catch (error) {
        console.error("Failed to fetch gallery data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAndGroupGalleryData();
  }, []);

  useEffect(() => {
    document.body.style.overflow = selectedImage ? "hidden" : "auto";
  }, [selectedImage]);

  const handleCategoryClick = (category) => {
    setActiveCategory(category);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBackToAlbums = () => setActiveCategory(null);
  const handleImageClick = (img) => setSelectedImage(img);
  const handleCloseLightbox = () => setSelectedImage(null);

  const getThumbnail = (album, index) => {
    return album.images[index]?.url || null;
  };

  return (
    <>
      <Helmet>
        <title>
          Photo Gallery | Sucheta Kriplani Teacher Training College (SKTTC),
          Jodhpur
        </title>

        <meta
          name="description"
          content="Explore campus photos, B.Ed academic activities, cultural programs, PTET events, infrastructure, laboratories, library, and student life at Sucheta Kriplani Teacher Training College (SKTTC), Manaklao, Jodhpur."
        />

        <meta
          name="keywords"
          content="SKTTC Gallery, Sucheta Kriplani College Photos, B.Ed College Jodhpur Images, PTET Events Photos, Manaklao Campus Life, JNVU Affiliated College Gallery"
        />

        <link rel="canonical" href="https://suchetabedcollege.org/gallery" />

        <link rel="preload" as="image" href={gallery} />
      </Helmet>

      <div className="gallery-of-sk-college-wrapper">
        {/* --- UPDATED HEADER SECTION START --- */}
        <div className="gallery-of-sk-college-header-container">
          <img
            src={gallery}
            alt="SK College Gallery"
            className="gallery-of-sk-college-header-img"
          />

          <div className="gallery-of-sk-college-header-overlay">
            <h1 className="gallery-of-sk-college-title">Our Gallery</h1>
            <div className="gallery-of-sk-college-breadcrumb">
              <Link to="/" className="gallery-breadcrumb-link">
                {" "}
                Home
              </Link>
              <span className="gallery-of-sk-college-sep">/</span>
              <span className="gallery-of-sk-college-current">Gallery</span>
              {activeCategory && (
                <>
                  <span className="gallery-of-sk-college-sep">/</span>
                  <span className="gallery-of-sk-college-current">
                    {activeCategory.title}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
        {/* --- UPDATED HEADER SECTION END --- */}

        <div className="gallery-of-sk-college-content">
          {!activeCategory ? (
            loading ? (
              <div className="gallery-of-sk-college-loading">
                Loading Albums...
              </div>
            ) : albums.length === 0 ? (
              <div className="gallery-of-sk-college-loading">
                No Albums Found.
              </div>
            ) : (
              <div className="gallery-of-sk-college-albums-grid">
                {albums.map((album) => {
                  const mainThumb = getThumbnail(album, 0);
                  const subThumb1 = getThumbnail(album, 1);
                  const subThumb2 = getThumbnail(album, 2);
                  const subThumb3 = getThumbnail(album, 3);

                  return (
                    <div
                      key={album.id}
                      className="gallery-of-sk-college-album-card"
                      onClick={() => handleCategoryClick(album)}
                    >
                      <div className="gallery-of-sk-college-thumbs-layout">
                        <div className="gallery-of-sk-college-thumb-main">
                          {mainThumb ? (
                            <img
                              src={mainThumb}
                              alt="Main Preview"
                              loading="lazy"
                            />
                          ) : (
                            <div className="gallery-of-sk-college-thumb-empty main"></div>
                          )}
                        </div>
                        <div className="gallery-of-sk-college-thumb-row">
                          {subThumb1 ? (
                            <img src={subThumb1} alt="Sub 1" />
                          ) : (
                            <div className="gallery-of-sk-college-thumb-empty"></div>
                          )}
                          {subThumb2 ? (
                            <img src={subThumb2} alt="Sub 2" />
                          ) : (
                            <div className="gallery-of-sk-college-thumb-empty"></div>
                          )}
                          {subThumb3 ? (
                            <img src={subThumb3} alt="Sub 3" />
                          ) : (
                            <div className="gallery-of-sk-college-thumb-empty"></div>
                          )}
                        </div>
                      </div>

                      <div className="gallery-of-sk-college-album-info">
                        <h3 className="gallery-of-sk-college-album-title">
                          {album.title}
                        </h3>
                        <div className="gallery-of-sk-college-album-count">
                          <FaImages /> {album.images.length} Photos
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          ) : (
            <div className="gallery-of-sk-college-images-view">
              <button
                className="gallery-of-sk-college-back-btn"
                onClick={handleBackToAlbums}
              >
                <FaArrowLeft /> Back to Albums
              </button>
              <h2 className="gallery-of-sk-college-cat-title">
                {activeCategory.title}
              </h2>
              <div className="gallery-of-sk-college-grid">
                {activeCategory.images.length === 0 ? (
                  <div className="gallery-of-sk-college-loading">
                    No Images Found.
                  </div>
                ) : (
                  activeCategory.images.map((img, index) => (
                    <div
                      key={index}
                      className="gallery-of-sk-college-item"
                      onClick={() => handleImageClick(img)}
                    >
                      <img src={img.url} alt={img.desc} loading="lazy" />
                      <div className="gallery-of-sk-college-overlay">
                        <FaExpand />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {selectedImage && (
          <div
            className="gallery-of-sk-college-lightbox"
            onClick={handleCloseLightbox}
          >
            <div
              className="gallery-of-sk-college-lightbox-content"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="gallery-of-sk-college-lightbox-image-container">
                <img src={selectedImage.url} alt="Full View" />
                <button
                  className="gallery-of-sk-college-close-btn"
                  onClick={handleCloseLightbox}
                >
                  <FaTimes />
                </button>
              </div>
              <div className="gallery-of-sk-college-lightbox-desc">
                <p>{selectedImage.desc}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default Gallery;
